import invariant from 'tiny-invariant';
import { prefetchCategoryImages } from '~/modules/category/util/prefetch-category-images';
import {
    getCanonicalHreflang,
    createMetaTitle,
    createMetaDescription,
    fullCountryNameFromCode,
    createOGTags,
    mergeMeta,
} from '~/modules/seo';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createProductsItemListSchema } from '~/modules/seo/schema/item-list-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { FAQSection } from '@modules/category/component/faq-section/FaqSection';
import type { DataFunctionArgs } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { getDirectoryPageMeta } from '@modules/category/api';
import { useLoaderData } from '@remix-run/react';
import { HotButtons, pxToRem } from '@archipro-design/aria';
import { json } from '@remix-run/node';
import {
    useTheme,
    Container,
    Box,
    Breadcrumb,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import { TileGrid } from '@modules/category/component/tile-grid/TileGrid';
import { DirectoryItemEnumType, PermissionEnum } from 'generated/graphql';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import { RecentlyViewed } from '@modules/category/component/recently-viewed/RecentlyViewed';
import { getElasticSearchResults } from '@modules/search/api/elasticsearch.server';
import type {
    ElasticSearchAggregateProps,
    ElasticSearchAggregations,
    ElasticSearchQueryProps,
    ElasticSearchSortOptions,
    SearchResults,
} from '@modules/search/type/api';
import type { SearchResultItem } from '@modules/search/type/hits';
import { mapESProductTileViewModel } from '@modules/search/util/elasticsearch-product-tile-view';
import type { ProductTileViewModel } from '@modules/search/util/elasticsearch-product-tile-view';
import { CategoryFilterSection } from '@modules/category/component/category-filter-section/CategoryFilterSection';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import { createFilterStates } from '@modules/search/util/create-filter-states.server';
import { Link, useSearchParams } from '@remix-run/react';
import { adRequest } from '@modules/adverts/api';
import AdvertBannerTile from '@modules/adverts/component/advert-banner-tile/AdvertBannerTile';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { getRecentlyViewed } from '~/modules/category/api/get-recently-viewed';
import { SubFooterSection } from '~/modules/category/component/sub-footer/SubFooter';
import ShoppingAvailable from '~/modules/tile/component/product-tile/ShoppingAvailable';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import VariantDot from '~/modules/tile/component/product-tile/VariantDot';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import {
    USER_PREFERENCE_KEY,
    useSearchFilters,
} from '~/modules/search/hook/use-search-filters';
import type { FilterStatesKey } from '~/modules/search/type/filter-state';
import AppliedFilters from '@modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import { logDirectoryItemClick } from '~/modules/category/util/log-directory-item-click';
import { useTracker } from '@archipro-website/tracker';
import { useEffect } from 'react';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import ProductTile from '~/modules/tile/component/product-tile/ProductImpressionTile';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import { getSession } from '~/modules/root/util/session.server';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import { getEcomEventItemList } from '~/modules/tracking/util/gtmHelper';
import { hasFeature, useTileHotButtons, type APHandle } from '~/modules/root';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProductCategories } from '~/modules/products-category/api';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { getString, parseURL } from '@archipro-website/top-navigation';
import { validateSort } from '~/modules/search/util/apply-es-search-sort';
import DetailedProductSpliter from '~/modules/products-category/component/DetailedProductSpliter';
import ArchizeenAdvertTile from '~/modules/tile/component/archizeen-tile/ArchizeenAdvertTile';
import { getArticleTileViewModelFromAd } from '~/modules/adverts/utils/map-ad-data-to-tile.server';
import type { ArticleTileViewModel } from '~/modules/article';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { noIndexProductCategory } from '~/modules/seo/util/no-index-product-category';
import { trackBreadcrumbClick } from '~/modules/tracking/util/trackBreadcrumbClick';
import { isFirstPage } from '~/modules/search/util/search-param-helper';
import type { GA4ProductData } from '~/modules/tracking/util/ga4Helper';
import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import { getPermissions } from '~/modules/user';
import findLastIndex from 'lodash.findlastindex';
import AwardIndicator from '@modules/page-builder/component/detail/banner-awards/AwardIndicator';
import { getUrlHasFilters } from '~/modules/search/util/get-url-has-filters.server';
import {
    DIRECTORY_IMAGE_WIDTH_DESKTOP_QUARTER,
    DIRECTORY_IMAGE_WIDTH_MOBILE_QUARTER,
    getDirectoryTileImageProps,
} from '@modules/root/util/get-directory-tile-image-props';
import BackToTopButton from '@modules/directory/component/BackToTopButton';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;
        const { rootData, appData } = getRouteData(matches);
        const imageConfigProps = appData?.imageConfigProps;

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.directoryPageMeta?.category
                    ? `Buy ${data?.directoryPageMeta?.category} Online`
                    : 'Page Not Found',
            ],
            true,
            data?.pagination?.page
        );

        invariant(
            rootData?.localisation.country,
            'rootData is required for product category SEO'
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            ` Browse a wide range of ${
                data?.directoryPageMeta?.category
            } on ArchiPro. We offer high-quality products from leading brands in ${fullCountryNameFromCode(
                rootData.localisation.country
            )}. Shop now!`,
        ]);

        const productsListSchema = createProductsItemListSchema(props);
        const FAQSchema = createFAQSchema(data?.localisation?.FAQSchemas ?? []);
        const ldSchema = createLDSchema([productsListSchema, FAQSchema]);

        const numberTilesToPreload = data?.appDisplayMode === 'desktop' ? 4 : 2;
        const preloadImageLinks = data?.searchResult?.hits
            ? prefetchCategoryImages(
                  data.searchResult.hits,
                  numberTilesToPreload,
                  getImagesSizes({
                      desktop: DIRECTORY_IMAGE_WIDTH_DESKTOP_QUARTER,
                      mobile: DIRECTORY_IMAGE_WIDTH_MOBILE_QUARTER,
                  }),
                  data?.appDisplayMode === 'mobile',
                  imageConfigProps
              )
            : [];

        const metaDescriptors = [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
                type: 'website',
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...preloadImageLinks,
        ];

        return noIndexProductCategory(
            metaDescriptors,
            data?.url ?? '',
            data?.productCategory.customSeoOnFilters,
            appData?.user.FeatureFlags
        );
    }
);

export const handle: APHandle = {
    isCategoryPage: true,
};

const CATEGORY_IGNORE_FILTER_KEYS: FilterStatesKey[] = [
    'libraryRanges',
    'designers',
    'brands',
    'awards',
];

const DEFAULT_PRODUCT_PAGE_SIZE = 40;
const DEFAULT_SORT: ElasticSearchSortOptions = 'featured';
export const PRODUCT_TILE_EXPANDED_WIDTH = 368;
export const PRODUCT_TILE_COLLAPSED_WIDTH = 340;
export const PRODUCT_TILE_PAGING_WIDTH = 175;
export const PRODUCT_TILE_BULLET_SIZE = 11;

export const loader = async (args: DataFunctionArgs) => {
    const { request } = args;
    const { appDisplayMode, config } = args.context;
    const url = new URL(request.url);
    const searchParams = url.searchParams;
    const session = await getSession(request);
    const isDesktop = appDisplayMode === 'desktop';

    const { basePath, params } = parseURL(url.pathname + url.search);
    const urlSort = validateSort(
        params ? getString(params, 'sort') : undefined
    );

    const hasFilters = getUrlHasFilters(url);

    const queryES = async (pageSize: number) => {
        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname}?${searchParams.toString()}`,
            'product',
            { visibilities: ['InLibrary'], pageSize },
            DEFAULT_SORT
        );

        const updateUserPreference =
            searchParams.get(USER_PREFERENCE_KEY) === '1';

        if (updateUserPreference) {
            session.setUserPreferences({
                forSale: queryParams.filters?.isPurchasable ?? false,
            });
            searchParams.delete(USER_PREFERENCE_KEY);
            throw redirect(`${url.pathname}?${searchParams.toString()}`, {
                headers: await session.getHeaders(),
            });
        }

        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });

        const getSearchResult = () => {
            return getElasticSearchResults<
                SearchResults<SearchResultItem>,
                ElasticSearchQueryProps
            >({ ...args.context, path: 'search', queryParams, request });
        };

        const getAggregations = () => {
            return aggregationProps
                ? getElasticSearchResults<
                      ElasticSearchAggregations,
                      ElasticSearchAggregateProps
                  >({
                      ...args.context,
                      path: 'aggregate',
                      queryParams: aggregationProps,
                      request,
                  })
                : undefined;
        };

        return Promise.all([
            queryParams,
            aggregationProps,
            getSearchResult(),
            getAggregations(),
        ]);
    };

    const promotedAdsPromise = adRequest({
        type: 'PromotedDirectoryPlacement',
        requestUrl: request.url,
        addPathContext: ['region'],
        ...args.context,
    });

    let [
        user,
        directoryPageMeta,
        productCategory,
        recentlyViewed,
        ads,
        localisation,
        promotedAds,
        esResults,
    ] = await Promise.all([
        args.context.getUser(),
        getDirectoryPageMeta({
            ...args.context,
            url,
        }),
        getProductCategories({
            ...args.context,
            args,
            config,
        }),
        getRecentlyViewed(
            { type: DirectoryItemEnumType.Product, limit: 16 },
            args
        ),
        adRequest({
            type: 'CategoryPlacement',
            requestUrl: request.url,
            addPathContext: ['region'],
            ...args.context,
        }),
        getPageLocalisation(args),
        promotedAdsPromise,
        // Waterfall call to query Promoted Ads and products.
        // We need to wait for Promoted Ads to be able to determine if we need to fetch one less product.
        (async () => {
            const promotedAdsResult = await promotedAdsPromise;
            return queryES(
                isDesktop &&
                    isFirstPage(searchParams) &&
                    promotedAdsResult &&
                    !hasFilters &&
                    promotedAdsResult.length > 0 // promoted article just for desktop atm. might add mobile later.
                    ? DEFAULT_PRODUCT_PAGE_SIZE - 1
                    : DEFAULT_PRODUCT_PAGE_SIZE
            );
        })(),
    ]);

    const [queryParams, aggregationProps, searchResult, aggregations] =
        esResults;

    const hasMarketingView = getPermissions(
        user,
        PermissionEnum.BmMarketingView
    );

    const facetFilterFeatureEnabled = hasFeature(
        user.FeatureFlags,
        'filter_facet'
    );

    const searchParamsForSale = queryParams.filters?.isPurchasable ?? false;
    // block forSale preference for users without shopnav feature
    const userPreferenceForSale =
        session.getUserPreferences()?.forSale ?? false;

    if (!searchParamsForSale && userPreferenceForSale) {
        // if we have forSale items, forSale filter not applied and user prefer to see forSale items, add the forSale filter
        searchParams.set('forSale', '1');
        throw redirect(`${url.pathname}?${searchParams.toString()}`);
    }

    const pagination = createPaginationState({
        request,
        totalCount: searchResult?.nbHits ?? 0,
        maxDisplayCount: searchResult?.maxResultWindow,
        defaultPageSize: DEFAULT_PRODUCT_PAGE_SIZE,
    });

    const productTiles =
        searchResult?.hits?.reduce((tiles, hit) => {
            if (hit.PType === 'Product')
                tiles.push(mapESProductTileViewModel(hit, user));
            return tiles;
        }, [] as ProductTileViewModel[]) ?? [];

    const adsShuffled = ads ? shuffleArray(ads) : [];

    const promotedAdTiles =
        shuffleArray(promotedAds)?.reduce((acc, ad) => {
            const tile = getArticleTileViewModelFromAd(ad);
            if (tile) {
                acc.push(tile);
            }
            return acc;
        }, [] as ArticleTileViewModel[]) ?? [];

    const filterStates = aggregations
        ? createFilterStates({
              aggregations,
              appliedFilters: queryParams.filters,
              aggregationsParams: aggregationProps?.aggregations,
              searchType: 'product',
              categoryLink: basePath,
              rootText: 'All',
              sortBy: urlSort,
              facetFilterFeatureEnabled,
          })
        : null;

    // we want to show ads even though the location filter is applied.
    const appliedFilterCount = filterStates
        ? getAppliedFilterNumber(filterStates, ['searchLocation'])
        : 0;

    const enableAwardBadges = hasFeature(user.FeatureFlags, 'award_badges');

    return json(
        {
            appDisplayMode,
            directoryPageMeta,
            searchResult,
            productCategory,
            productTiles,
            pagination,
            recentlyViewedProducts: recentlyViewed ?? [],
            categoryDescription: directoryPageMeta?.directoryHeaderDescription,
            ads: adsShuffled,
            promotedAds: promotedAdTiles,
            localisation,
            url: url.pathname + url.search,
            filterStates,
            appliedFilterCount,
            enableAwardBadges,
            hasMarketingView,
        },
        {
            headers: await session.getHeaders(),
        }
    );
};

const ProductsCategoryPage = () => {
    const theme = useTheme();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop, mobile } = appDisplayMode;
    const {
        categoryDescription,
        directoryPageMeta,
        productTiles,
        productCategory,
        pagination,
        recentlyViewedProducts,
        filterStates,
        ads,
        promotedAds,
        appliedFilterCount,
        enableAwardBadges,
        hasMarketingView,
    } = useLoaderData<typeof loader>();
    const tracker = useTracker();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(ads, ['region']);
    const promotedAdsMemoized = useLocationMemo(promotedAds, ['region']);
    const [searchParams] = useSearchParams();

    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedCompanies = searchParams.getAll('professionals').join(' | ');
    useEffect(() => {
        tracker.log('productListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                brand: selectedCompanies,
            },
        });
    }, [selectedCategory, selectedCompanies, tracker]);

    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const productIDs = productTiles.map((product) => product.tile.id);
    const { getTileHotButtons, hotButtonModal, getTileAdminCaption } =
        useTileHotButtons({
            itemType: 'product',
            itemIDs: productIDs,
        });

    const { favouriteItem } = useSaveToDesignBoard('ProductPin');

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    const gridColumns = desktop ? (filterDrawerOpen ? 4 : 5) : 2;
    const gridColumnWidth = filterDrawerOpen
        ? PRODUCT_TILE_COLLAPSED_WIDTH
        : PRODUCT_TILE_EXPANDED_WIDTH;
    const gridColumnPagingSpacer =
        (gridColumnWidth - PRODUCT_TILE_PAGING_WIDTH) / 2;

    const searchFilters = useSearchFilters({
        category: 'product',
        initialStates: filterStates,
        isCategoryPage: true,
        ignoreFilterKeys: CATEGORY_IGNORE_FILTER_KEYS,
    });

    const showSlider = useABTestIsOn('directory-tile-slider');
    const showDownloads = useABTestIsOn('directory-tile-downloads');

    const tiles = productTiles.map((product, index) => {
        const { tile } = product;

        const {
            productLink,
            professionalName,
            canPurchase,
            variants,
            professionalID,
            id,
            productName,
            price,
            pricePrefix,
            downloads,
        } = tile;

        const hotButtons = getTileHotButtons({
            item: tile,
            onDeleteClick: () => willDelete(id),
        });

        const adminCaption = getTileAdminCaption(id);

        const topActions = [];

        if (hotButtons) {
            topActions.push(
                <HotButtons key="hot-buttons" items={hotButtons} />
            );
        }

        if (canPurchase) {
            topActions.push(
                <ShoppingAvailable inline={true} key="shopping-available" />
            );
        }

        const ga4ProductData: GA4ProductData | undefined = canPurchase
            ? {
                  item_id: id,
                  item_name: productName,
                  item_variant:
                      (variants?.length > 0 ? variants[0]?.key : '') || '',
                  index: index,
                  price: price,
                  quantity: 1,
              }
            : undefined;

        const images = showSlider ? product.sliderImages : tile.image;
        const imageProps = getDirectoryTileImageProps(
            index,
            2,
            4,
            mobile,
            images,
            productName
        );

        const paddleIndent =
            imageProps.length < 5
                ? (5 - imageProps.length) * PRODUCT_TILE_BULLET_SIZE
                : 0;
        const paginationPadding = gridColumnPagingSpacer + paddleIndent;
        return (
            <ProductTile
                key={productLink}
                as={Link}
                to={productLink}
                prefetch="intent"
                id={id}
                price={price}
                pricePrefix={pricePrefix}
                productName={productName}
                image={imageProps}
                professionalName={professionalName}
                variant={desktop ? '01' : '02'}
                size={desktop && !filterDrawerOpen ? 'large' : 'medium'}
                onFavourite={(e) => favouriteItem.onClick(e, id)}
                isFavourited={favouriteItem.favourited(id)}
                variants={variants?.map((v) => ({
                    key: v.key,
                    children: <VariantDot {...v} size={desktop ? 13 : 9} />,
                }))}
                additionalVariantCount={
                    variants && variants.length > 4
                        ? { children: `+${variants.length - 4}`, variant: '05' }
                        : undefined
                }
                onClick={() => {
                    logDirectoryItemClick(tracker, {
                        itemID: product.productId,
                        offset: index,
                        categoryType: 'products',
                        appDisplayMode,
                    });
                }}
                impression={{
                    professionalID: professionalID,
                    itemID: product.productId,
                    type: 'LibraryItem_Directory',
                }}
                sectionSource={'Directory'}
                remarketingImpression={
                    ga4ProductData
                        ? getEcomEventItemList(
                              [ga4ProductData],
                              tile.priceCurrency
                          )
                        : undefined
                }
                adminCaption={adminCaption}
                topActions={topActions}
                variables={{
                    paginationPadding,
                }}
                newDesignEnabled={true}
                awardIndicator={
                    enableAwardBadges &&
                    tile.hasAwards && <AwardIndicator size={'sm'} />
                }
                downloads={showDownloads ? downloads : undefined}
            />
        );
    });

    if (
        desktop &&
        pagination.page === 1 && // show on the first page only
        tiles.length > 0 &&
        promotedAdsMemoized.length &&
        promotedAdsMemoized[0] &&
        promotedAdsMemoized[0].ad
    ) {
        const offset = filterDrawerOpen ? 7 : 9;
        if (tiles.length >= offset) {
            const adTile = promotedAdsMemoized[0];
            const professionalID = adTile.professionalID ?? 0;
            const imageProps = {
                src: adTile.image,
                width: '100%',
                height: '100%',
                sizes: getImagesSizes(310),
                alt: adTile.title,
            };
            tiles.splice(
                offset,
                0,
                <ArchizeenAdvertTile
                    tag={'ARTICLE'}
                    advertCreative={promotedAdsMemoized[0].ad}
                    key={`promoted_${adTile.ID}`}
                    prefetch="intent"
                    image={imageProps}
                    size={!filterDrawerOpen ? 'large' : 'medium'}
                    variant={'08'}
                    variables={{
                        color: theme.siteVariables.colors.charcoal['250'],
                        dynamicSizingWidth: 310,
                        dynamicSizingGapWidth: 0,
                        tilePadding: pxToRem(10),
                    }}
                    onFavourite={(e) =>
                        favouriteItem.onClick(e, professionalID)
                    }
                    isFavourited={favouriteItem.favourited(professionalID)}
                    title={adTile.title}
                    label={{
                        children: adTile.author,
                        variant: '06',
                    }}
                    date={{
                        children: adTile.date,
                        variant: '06',
                    }}
                    readTime={{
                        children: adTile.readTime,
                        variant: '06',
                    }}
                />
            );
        }
    }

    const category =
        directoryPageMeta.category !== 'Products'
            ? directoryPageMeta.category
            : undefined;
    if (searchFilters.filterStates.isPurchasable?.checked) {
        const lastPurchasbaleItemIndex = findLastIndex(
            productTiles,
            (t) => t.tile.canPurchase
        );
        const purchasableCount =
            productTiles?.filter((t) => t.tile.canPurchase).length ?? 0;
        if (
            purchasableCount === 0 ||
            (lastPurchasbaleItemIndex !== undefined &&
                lastPurchasbaleItemIndex !== -1)
        ) {
            const rowIndex = Math.ceil(
                (lastPurchasbaleItemIndex + 1) / gridColumns
            );
            const insertionIndex = rowIndex * gridColumns;
            if (insertionIndex < productTiles.length) {
                const moreProductsComing = (
                    <DetailedProductSpliter
                        key="MoreProductsComing"
                        category={category}
                        columns={gridColumns}
                    />
                );
                tiles.splice(insertionIndex, 0, moreProductsComing);
            }
        }
    }

    if (appliedFilterCount === 0 && adsMemoized.length && adsMemoized[0]) {
        tiles.unshift(
            <AdvertBannerTile
                key="AdvertBannerTile"
                advertCreative={adsMemoized[0]}
                columns={gridColumns}
                desktop={desktop}
            />
        );
    }

    return (
        <Box
            variables={{
                boxBackgroundColor:
                    theme.siteVariables.colorScheme.default?.background,
            }}
        >
            <Container>
                {desktop ? (
                    <TitleSectionDesktop
                        category={directoryPageMeta.directoryHeaderTitle}
                        categoryDescription={categoryDescription}
                        itemsInCategory={filterStates?.totalCount ?? 0}
                    />
                ) : (
                    <TitleSectionMobile
                        title={directoryPageMeta.directoryHeaderTitle}
                        categoryDescription={categoryDescription}
                        subtitle={
                            filterStates?.totalCount?.toLocaleString() ?? '0'
                        }
                    >
                        <TitleSectionFilterMobile
                            esCategory="product"
                            initialStates={filterStates}
                        />
                    </TitleSectionMobile>
                )}

                {desktop && (
                    <CategoryFilterSection
                        category="product"
                        searchFilters={searchFilters}
                        initialStates={filterStates}
                        filterDrawerOpen={filterDrawerOpen}
                        toggleFilterDrawer={toggleFilterDrawer}
                        componentBefore={
                            <Breadcrumb
                                logo={null}
                                home={{
                                    link: true,
                                    as: Link,
                                    label: 'Home',
                                    to: '/',
                                    onClick: () => {
                                        trackBreadcrumbClick(tracker, '/');
                                    },
                                }}
                                items={
                                    directoryPageMeta.breadcrumbs.map((b) => ({
                                        ...b,
                                        to: b.href,
                                        as: Link,
                                        onClick: () => {
                                            trackBreadcrumbClick(
                                                tracker,
                                                b.href
                                            );
                                        },
                                    })) ?? []
                                }
                            />
                        }
                    />
                )}

                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <CategoryFilterNav
                                category="product"
                                searchFilters={searchFilters}
                                hasMarketingView={hasMarketingView}
                            />
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                    content={
                        <div>
                            {desktop &&
                                searchFilters.appliedFilters &&
                                searchFilters.appliedFilters.length > 0 && (
                                    <AppliedFilters
                                        filterItems={
                                            searchFilters.appliedFilters
                                        }
                                        resetFilters={() =>
                                            searchFilters.resetFilters(
                                                resetFilterStates(
                                                    searchFilters.filterStates,
                                                    [
                                                        ...CATEGORY_IGNORE_FILTER_KEYS,
                                                        'category',
                                                    ]
                                                )
                                            )
                                        }
                                    />
                                )}
                            {tiles.length ? (
                                <TileGrid
                                    items={tiles}
                                    columns={gridColumns}
                                    columnWidth={gridColumnWidth}
                                    pagination={pagination}
                                    type="products"
                                />
                            ) : (
                                <NoResultsSection resultsType={'products'} />
                            )}
                        </div>
                    }
                />

                <RecentlyViewed
                    recentlyViewed={recentlyViewedProducts}
                    slidesPerView={desktop ? 5 : 2}
                    type={'products'}
                />

                <FAQSection
                    faqSchemaPanels={directoryPageMeta.faqSchemas}
                    footerContent={directoryPageMeta.footerContent}
                />

                <SubFooterSection
                    categories={productCategory.subfooterCategories ?? []}
                />

                {favouriteItem.target && (
                    <SaveToDesignBoard
                        {...favouriteItem}
                        autoReposition={false}
                    />
                )}

                {itemIDToDelete && (
                    <DeleteItemConfirmModal
                        title={'Delete product'}
                        content={
                            'Are you sure you would like to delete this product? All information and associated tags will be lost.'
                        }
                        open={true}
                        onCancel={cancelDelete}
                        onConfirm={confirmDelete}
                    />
                )}

                {hotButtonModal}
            </Container>

            {<BackToTopButton />}
        </Box>
    );
};

export default ProductsCategoryPage;
